import { addShortcodeToEditor, getShortcodeWrapper } from "@/assets/utilities/tinymce/shortodeAddition";
import { getLiveChatAutoTemplate } from "@/assets/utilities/tinymce/shortcodes/templates";
import { parseToHtml } from "@/utility/parseToHtml";
import { serializeDynamicComponents, visualizeDynamicComponents } from "@/assets/utilities/tinymce/visualization";
import { findInvalidTelHrefsDeep } from "@/utility/validateLiveChatTelHrefs";
import { showTelConfirmModal } from "@/assets/utilities/tinymce/helpers/telConfirmModal";
import i18n from "@/i18n";

function serializeContent(obj) {
  if (typeof obj !== "object" || obj === null) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(item => serializeContent(item));
  } else {
    const newObj = {};
    Object.keys(obj).forEach(key => {
      if (key === "_telHrefError") return;
      if (key === "content") {
        const content = parseToHtml(obj[key]);
        serializeDynamicComponents(content);
        newObj[key] = content.body.innerHTML;
      } else if (key === "text") {
        const content = parseToHtml(obj[key]);
        serializeDynamicComponents(content);
        newObj[key] = content.body.innerHTML;
      } else {
        newObj[key] = serializeContent(obj[key]);
      }
    });

    return newObj;
  }
}

function visualizeContent(obj) {
  if (typeof obj !== "object" || obj === null) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(item => visualizeContent(item));
  } else {
    const newObj = {};
    Object.keys(obj).forEach(key => {
      if (key === "content") {
        const content = parseToHtml(obj[key]);
        visualizeDynamicComponents(content);
        newObj[key] = content.body.innerHTML;
      } else if (key === "text") {
        const content = parseToHtml(obj[key]);
        visualizeDynamicComponents(content);
        newObj[key] = content.body.innerHTML;
      } else {
        newObj[key] = visualizeContent(obj[key]);
      }
    });

    return newObj;
  }
}

const editLiveChatAutoConfig = (editor, shortcodeWrapper) => {
  let currentData = null;

  const liveChatWindow = editor.windowManager.openUrl({
    title: "Live Chat - Auto",
    url: "/live-chat-auto-dialog",
    buttons: [
      {
        type: "cancel",
        name: "close",
        text: "Cancel",
      },
      {
        type: "custom",
        name: "submit",
        text: "Submit",
        primary: true,
      },
    ],
    onAction: function (instance, message) {
      const { valid, errors } = findInvalidTelHrefsDeep(currentData);

      const save = () => {
        editor.windowManager.alert("Success!");
        const serializedData = serializeContent(currentData);
        const encodedData = btoa(unescape(encodeURIComponent(JSON.stringify(serializedData))));

        shortcodeWrapper.setAttribute("data-live-chat-auto-data", encodedData);
        // We have to call change function here to updateMainContent in form component
        editor.fire("change");
        instance.close();
      };

      if (!valid) {
        showTelConfirmModal(
          i18n.t("shortcodes.live_chat.invalid_tel_href_confirm", { count: errors.length }),
          errors.map(e => e.invalidHrefs.join(", ")).join(", "),
        ).then(confirmed => {
          if (confirmed) save();
          else instance.sendMessage({ type: "telHrefValidationFailed" });
        });
        return;
      }

      save();
    },
    onMessage: (instance, message) => {
      if (message.mceAction === "requestData") {
        sendData();
      }

      if (message.mceAction === "updateLiveChatData") {
        currentData = message.data;
      }
    },
    width: 1200,
    height: window.innerHeight - 20,
  });

  const sendData = () => {
    const liveChatShortcodeData = shortcodeWrapper.getAttribute("data-live-chat-auto-data");
    const parsedData = JSON.parse(decodeURIComponent(escape(window.atob(liveChatShortcodeData))));
    const content = visualizeContent(parsedData);

    liveChatWindow.sendMessage({
      type: "sendData",
      data: content,
    });
  };
};

const liveChatAutoConfig = (tinymce, uuid, shortcodeOnClick) => {
  let currentData = null;

  tinymce.activeEditor.windowManager.openUrl({
    title: "Live Chat - Auto",
    url: "/live-chat-auto-dialog?data=null",
    buttons: [
      {
        type: "cancel",
        name: "close",
        text: "Cancel",
      },
      {
        type: "custom",
        name: "submit",
        text: "Submit",
        primary: true,
      },
    ],
    onAction: function (instance) {
      const { valid, errors } = findInvalidTelHrefsDeep(currentData);

      const save = () => {
        const shortcodeWrapper = getShortcodeWrapper(
          uuid,
          shortcodeOnClick,
          getLiveChatAutoTemplate(),
          "live-chat-auto-shortcode",
        );
        const shortcodeWrapperEl = parseToHtml(shortcodeWrapper).querySelector(".shortcode");
        const serializedData = serializeContent(currentData);
        const encodedData = btoa(unescape(encodeURIComponent(JSON.stringify(serializedData))));

        shortcodeWrapperEl.setAttribute("data-live-chat-auto-data", encodedData);

        addShortcodeToEditor(shortcodeWrapperEl.outerHTML, uuid);
        instance.close();
      };

      if (!valid) {
        showTelConfirmModal(
          i18n.t("shortcodes.live_chat.invalid_tel_href_confirm", { count: errors.length }),
          errors.map(e => e.invalidHrefs.join(", ")).join(", "),
        ).then(confirmed => {
          if (confirmed) save();
          else instance.sendMessage({ type: "telHrefValidationFailed" });
        });
        return;
      }

      save();
    },
    onMessage: (instance, message) => {
      currentData = message.data;
    },
    width: 1200,
    height: window.innerHeight - 20,
  });
};

export { liveChatAutoConfig, editLiveChatAutoConfig };
